import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../shell/bridge';
import { fairnessCardHourOpen } from '../../../shared/fairness';
import type { FairnessCardPublic } from '../../../shared/types';
import { selectDecisionRail, type DecisionView } from '../../../shared/decisions';
import {
  LIFE_RAIL_MAX_VISIBLE,
  pruneLifeNudgeLedger,
  selectLifeRail,
  takeLifeNudge,
  type LifeChore,
  type LifeNudgeLedger,
} from '../../../shared/life-rail';

/**
 * LifeRail.tsx — chores on the Thread as a collapsible rail (card
 * FiaOoQ0mb11g · EPIC 2 story 2.4).
 *
 * System output, so it is its own block with its own heading: it never draws
 * as a chat bubble and never sits inside the message stream. The score, the
 * 6-day window and the 1-push/1-in-feed nudge cap all live in
 * shared/life-rail.ts — this file only fetches, lays out and persists.
 *
 * The rail is ambient: it shows the top three scored chores and collapses the
 * rest into "Life (n)", which jumps to Life > Chores. It spends no nudge —
 * the in-feed nudge is spent once per chore per round on the first sight of
 * something overdue, and the marker that says so stays until the round ends.
 * The `push` half of the cap is spent by the nudge engine when it lands; the
 * budget it must use is already written and unit-tested here.
 */

const LEDGER_KEY = 'pear_life_nudge_ledger';

function loadLedger(): LifeNudgeLedger {
  try {
    const raw = localStorage.getItem(LEDGER_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as LifeNudgeLedger;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function saveLedger(ledger: LifeNudgeLedger): void {
  try {
    localStorage.setItem(LEDGER_KEY, JSON.stringify(ledger));
  } catch {
    /* private mode / quota — the rail degrades to no memory, never throws */
  }
}

/** "Overdue · Tue" / "Due Thu" / "Due today" — read in the client's own
 *  timezone, the only one a date-only chore row can carry (card ZgLp6PjCXHEm). */
function whenLabel(dueAt: number | null, now: number): string {
  if (dueAt == null) return 'No date';
  const DAY = 86_400_000;
  const startOf = (t: number) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };
  const days = Math.round((startOf(dueAt) - startOf(now)) / DAY);
  const stamp = new Date(dueAt).toLocaleDateString(undefined, { weekday: 'short' });
  if (days < 0) return `Overdue · ${stamp}`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `Due ${stamp}`;
}

/** The rail's compact outcome line — the full sentence lives in the Life rail. */
function stateLineForRail(v: DecisionView): string {
  if (v.state === 'tied') return 'You picked differently — it is on the Plan agenda.';
  if (v.state === 'revealed') return v.outcomeLine ?? 'Both of you picked.';
  return v.stalled ? 'Still undecided — it is on the Plan agenda.' : 'Waiting on both picks.';
}

interface LifeRailProps {
  /** Display names for the assignee line (card yFLCjm7ONIEc). */
  nameFor?: (memberId: string | null | undefined) => string | null;
  /** "Life (n)" -> Life > Chores. Optional so Thread compiles standalone. */
  onOpenLife?: () => void;
}

export function LifeRail({ nameFor, onOpenLife }: LifeRailProps) {
  const [chores, setChores] = useState<LifeChore[] | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [nudgedIds, setNudgedIds] = useState<Set<string>>(() => new Set());
  /* Task Assist (spec kZX3gsJkKaTH): the couple-level consent switch, read
     from the same Settings payload the Settings screen uses — one row, so both
     partners read the same answer. */
  const [assist, setAssist] = useState<{ enabled: boolean; offer: boolean } | null>(null);
  /** The offer card stays on screen until it is answered even after the
   *  one-shot stamp lands, so it can never flash and vanish. */
  const [offerLive, setOfferLive] = useState(false);
  const stampedRef = useRef(false);
  /* Answered in THIS session (card AQLrop9EOoso). `offerLive` is cleared the
     moment either button is tapped, but the rail is live while that happens:
     the settings read that answered the click, a `pear:chores` broadcast, or a
     focus refetch can all resolve with a payload that still carried
     `offer:true` — read before the write landed — and the effect below would
     happily put the card back. Once a person has answered, no later read gets
     to re-offer it. The PARTNER answering still works: that comes in as
     `offer:false`, which the effect already treats as "not live". */
  const answeredRef = useRef(false);
  /* Drift card (spec kZX3gsJkKaTH §3, slice C): the ONE question the fairness
     engine is allowed to ask, read from the same rail fetch's sibling route.
     Both partners see this identical card — it is one row on the server, so
     there is no private version of the question. */
  const [fairCard, setFairCard] = useState<FairnessCardPublic | null>(null);
  /* At most ONE open decision rides the rail (parent card VcBi3nr9GjBj, child
     C): a decision is a couple-level thing waiting on a person, so it belongs
     in the same ambient stack as a chore — but a stack of them would turn the
     rail into a to-do list the couple never agreed to. The rest are a count. */
  const [decision, setDecision] = useState<DecisionView | null>(null);
  const [decisionCount, setDecisionCount] = useState(0);
  /** Set while an answer is in flight, so a double tap cannot send two. */
  const [fairBusy, setFairBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api<{ chores: LifeChore[] }>('/api/chores');
      setChores(data.chores ?? []);
    } catch (e) {
      console.error('[Pear] life rail load failed', e);
      setChores([]);
    }
    // Consent rides on its own fetch: a failure here must not blank the rail,
    // and an absent answer reads as OFF, never as yes.
    try {
      const s = await api<{ settings?: { taskAssist?: { enabled: boolean; offer: boolean } } }>('/api/settings');
      setAssist(s.settings?.taskAssist ?? { enabled: false, offer: false });
    } catch {
      setAssist({ enabled: false, offer: false });
    }
    // Decisions ride their own fetch for the same reason: no decisions is the
    // correct degradation, never a blank rail.
    try {
      const d = await api<{ decisions: DecisionView[] }>('/api/decisions');
      const sel = selectDecisionRail(d.decisions ?? []);
      setDecision(sel.visible);
      setDecisionCount(sel.openCount);
    } catch {
      setDecision(null);
      setDecisionCount(0);
    }
    // The drift card rides its own fetch for the same reason: a rail with no
    // card is the correct degradation, never a blank screen.
    try {
      const f = await api<{ card?: FairnessCardPublic | null }>('/api/chores/fairness');
      setFairCard(f.card ?? null);
    } catch {
      setFairCard(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* The other partner can answer the offer or flip the switch from their own
     device, and this rail is live while they do it — the DO broadcasts on the
     'chores' frame for both. */
  useEffect(() => {
    const reload = () => { void load(); };
    window.addEventListener('pear:chores', reload);
    window.addEventListener('pear:decisions', reload);
    window.addEventListener('focus', reload);
    window.addEventListener('visibilitychange', reload);
    return () => {
      window.removeEventListener('pear:chores', reload);
      window.removeEventListener('pear:decisions', reload);
      window.removeEventListener('focus', reload);
      window.removeEventListener('visibilitychange', reload);
    };
  }, [load]);

  useEffect(() => {
    if (!assist?.offer) return;
    /* A person already answered in this session: a stale read that still says
       `offer:true` does not get to re-offer the question (card AQLrop9EOoso). */
    if (answeredRef.current) return;
    setOfferLive(true);
    if (stampedRef.current) return;
    stampedRef.current = true;
    // One-shot: the stamp means it cannot arrive again on any device. The card
    // already on screen is answered in this session.
    void api('/api/settings/joint', {
      method: 'PUT',
      body: { taskAssistOffer: 'shown' },
    }).catch(() => { stampedRef.current = false; });
  }, [assist?.offer]);

  const answerAssist = async (on: boolean) => {
    answeredRef.current = true;
    setOfferLive(false);
    setAssist(a => ({ enabled: on || (a?.enabled ?? false), offer: false }));
    try {
      await api('/api/settings/joint', {
        method: 'PUT',
        body: on ? { taskAssist: true } : { taskAssistOffer: 'no' },
      });
    } catch (e) {
      // A failed answer must not leave the card gone and the switch off with
      // nothing said — re-read the truth from the server. Un-latch first, or
      // the re-read could not bring the question back even when it should.
      answeredRef.current = false;
      console.error('[Pear] task assist answer failed', e);
      void load();
    }
  };

  const now = useMemo(() => Date.now(), [chores]);
  /* The drift card takes one of the rail's three slots (spec §3: it lands
     INSIDE the existing cap, never beside it) — so with a card on screen the
     chores above it get two. The card is drawn only in the couple's own
     daytime, and a card whose window has closed simply waits for morning:
     it is in-feed, so nothing is lost but the hour. */
  const fairVisible = fairCard !== null && fairnessCardHourOpen(new Date(now).getHours());
  const selection = useMemo(
    () => selectLifeRail(chores ?? [], now, fairVisible ? LIFE_RAIL_MAX_VISIBLE - 1 : LIFE_RAIL_MAX_VISIBLE),
    [chores, now, fairVisible],
  );

  /* Answer the drift card. Both answers are one POST, and the card leaves the
     screen immediately — the broadcast that follows is what re-reads the rail
     on the partner's device. A failure re-reads the server rather than
     leaving a card that has already been answered. */
  const answerFairCard = async (action: 'accept' | 'dismiss') => {
    if (fairBusy || !fairCard) return;
    setFairBusy(true);
    setFairCard(null);
    try {
      await api(`/api/chores/fairness/${action}`, { method: 'POST', body: {} });
    } catch (e) {
      console.error('[Pear] fairness card answer failed', e);
      void load();
    } finally {
      setFairBusy(false);
    }
  };

  /* First sight of something overdue spends the one in-feed nudge this round
     allows; the marker is then read back from the ledger, so a re-run of this
     effect (StrictMode, refetch) draws the same card instead of losing it. */
  useEffect(() => {
    if (!chores) return;
    const ledger = loadLedger();
    pruneLifeNudgeLedger(ledger, chores);
    const nudged = new Set<string>();
    for (const chore of chores) {
      if (chore.status !== 'open' || chore.dueAt == null) continue;
      if (chore.dueAt >= now) continue;
      takeLifeNudge(ledger, chore, 'in-feed');
      if ((ledger[chore.choreId]?.inFeed ?? 0) > 0) nudged.add(chore.choreId);
    }
    saveLedger(ledger);
    setNudgedIds(nudged);
  }, [chores, now]);

  // The offer can be the only thing worth drawing: a couple whose chores are
  // all clear still gets asked, once.
  if (!chores || (selection.eligibleCount === 0 && !offerLive && !fairVisible && !decision)) return null;

  const { visible, hiddenCount } = selection;
  /* Open decisions are part of what is waiting on a person, so the head count
     is chores + open decisions — one number, not two competing badges. */
  const waitingCount = selection.eligibleCount + decisionCount;
  if (collapsed) {
    return (
      <section id="life-rail" className="pear-life-rail is-collapsed" data-life-rail="" data-collapsed="1" aria-label="Life">
        <button
          type="button"
          id="life-rail-toggle"
          className="pear-life-rail-head"
          aria-expanded={false}
          aria-controls="life-rail-cards"
          onClick={() => setCollapsed(false)}
        >
          <span className="pear-life-rail-title">Life</span>
          <span className="pear-life-rail-count">{waitingCount}</span>
        </button>
      </section>
    );
  }

  return (
    <section id="life-rail" className="pear-life-rail" data-life-rail="" data-collapsed="0" aria-label="Life">
      <button
        type="button"
        id="life-rail-toggle"
        className="pear-life-rail-head"
        aria-expanded={true}
        aria-controls="life-rail-cards"
        onClick={() => setCollapsed(true)}
      >
        <span className="pear-life-rail-title">Life</span>
        <span className="pear-life-rail-count">{waitingCount}</span>
        <span className="pear-life-rail-collapse" aria-hidden="true">–</span>
      </button>

      <div id="life-rail-cards" className="pear-life-rail-cards">
        {/* ── One open decision (parent card VcBi3nr9GjBj, child C) — a
            couple-level choice still waiting for a pick. Tapping it opens Life,
            where the two options and the sealed state live; the rail never
            carries a vote button, because a rail tap should not decide
            something on the strength of a glance. ── */}
        {decision && (
          <article
            className="pear-life-card is-decision"
            id="life-rail-decision"
            data-life-decision={decision.decisionId}
            aria-label="Decision"
            onClick={() => onOpenLife?.()}
          >
            <div className="pear-life-main">
              <span className="pear-life-title">{decision.title}</span>
              <span className="pear-life-when">
                {decision.votes.length === 0
                  ? 'Neither of you has picked yet.'
                  : decision.votes.length === 1
                    ? 'One of you has picked — the other is still open.'
                    : stateLineForRail(decision)}
              </span>
            </div>
            <div className="pear-life-side">
              <span className="pear-life-chip">Decision</span>
            </div>
          </article>
        )}
        {/* ── Task Assist offer (spec kZX3gsJkKaTH) — asked once, in the rail,
            because the rail is where chores already live. Yes turns the
            consent switch on; Not now is a real answer and is never repeated.
            Identical on both partners' screens: there is no private version of
            this question. ── */}
        {offerLive && (
          <article
            className="pear-life-card is-assist"
            id="life-rail-task-assist"
            data-life-assist=""
            aria-label="Task Assist"
          >
            <div className="pear-life-main">
              <span className="pear-life-title">Task Assist</span>
              <span className="pear-life-when">
                Would you like Pear to monitor how chores and tasks are going?
                If it looks like something is sliding, I’ll suggest an approach
                that may work better for everyone.
              </span>
              <div className="pear-life-assist-actions">
                <button
                  type="button"
                  id="life-assist-yes"
                  className="pear-btn pear-btn-mustard"
                  onClick={() => void answerAssist(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  id="life-assist-no"
                  className="pear-btn"
                  onClick={() => void answerAssist(false)}
                >
                  Not now
                </button>
              </div>
            </div>
          </article>
        )}
        {/* ── Drift card (spec kZX3gsJkKaTH §3, slice C): the one question the
            fairness engine may ask, and it asks it identically on both
            screens. No counts, no deficit, no verdict against either partner
            — the card offers a move rather than stating a fault, which is the
            whole difference between a nudge and ammunition. ── */}
        {fairVisible && fairCard && (
          <article
            className="pear-life-card is-assist"
            id="life-rail-fairness"
            data-life-fairness=""
            aria-label="Chore balance"
          >
            <div className="pear-life-main">
              <span className="pear-life-title">Household</span>
              <span className="pear-life-when">
                A few tasks have been sitting for three weeks. Want me to move one?
              </span>
              <div className="pear-life-assist-actions">
                <button
                  type="button"
                  id="life-fairness-move"
                  className="pear-btn pear-btn-mustard"
                  disabled={fairBusy}
                  onClick={() => void answerFairCard('accept')}
                >
                  Move {`‘${fairCard.choreTitle}’`} to {nameFor?.(fairCard.toMemberId) ?? 'your partner'}
                </button>
                <button
                  type="button"
                  id="life-fairness-dismiss"
                  className="pear-btn"
                  disabled={fairBusy}
                  onClick={() => void answerFairCard('dismiss')}
                >
                  Not now
                </button>
              </div>
            </div>
          </article>
        )}
        {visible.map((chore) => {
          const isNudged = nudgedIds.has(chore.choreId);
          const late = chore.dueAt != null && chore.dueAt < now;
          return (
            <article
              className={`pear-life-card${isNudged ? ' is-nudged' : ''}`}
              key={chore.choreId}
              data-life-card=""
              data-chore-id={chore.choreId}
              data-nudged={isNudged ? '1' : '0'}
            >
              <div className="pear-life-main">
                <span className="pear-life-title">{chore.title}</span>
                <span className="pear-life-when">{whenLabel(chore.dueAt, now)}</span>
              </div>
              <div className="pear-life-side">
                <span className="pear-life-who">
                  {chore.assigneeId
                    ? nameFor?.(chore.assigneeId) ?? 'Your partner'
                    : 'Anyone'}
                </span>
                {isNudged && <span className="pear-life-chip is-nudged">Nudged once</span>}
                {!isNudged && late && <span className="pear-life-chip">Overdue</span>}
              </div>
            </article>
          );
        })}

        {hiddenCount > 0 && (
          <button
            type="button"
            id="life-rail-more"
            className="pear-life-more"
            onClick={() => onOpenLife?.()}
          >
            Life ({hiddenCount})
          </button>
        )}
      </div>
    </section>
  );
}
